import { Box, IconButton, InputAdornment, makeStyles, Paper, TextField, Tooltip, Typography } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import SearchIcon from '@material-ui/icons/Search';
import { TreeView } from "@material-ui/lab";
import { ChevronRight, ExpandMore } from "@material-ui/icons";
import StyledTreeItem from "../bluePrintComponents/BottomDrawer/StyledTreeItem";
import AddIcon from '@material-ui/icons/Add';
import { setFocusElement } from "../../../store/global/actions";
import { useDispatch, useSelector } from "react-redux";
import { useBottomDrawer } from "../../../hooks/useBottomDrawer";
import useDebounce from "../../../hooks/useDebounce";
import { getViews } from "../../../store/views/selectors";

const useStyles = makeStyles((theme) => ({
    addClusterlet: {
        '&:focus': {
            outline: 'none',
        },
    },
    smallText: {
        fontSize: '0.8rem',
    },

    eventDescription: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.3),
    },
}))

const ViewTree = ({data}) => {
    const {setActiveTab} = useBottomDrawer();
    const nodeIds = new Set([
        'sections',
        'widgets',
        'datasource',
        'fields'
    ])
    const classes = useStyles()
    const dispatch = useDispatch();
    const [filteredData, setFilteredData] = useState(data)
    const [searchValue, setSearchValue] = useState('')
    const [selectedNodeId, setSelectedNodeId] = useState()
    const debouncedSearchValue = useDebounce(searchValue, 400)

    function searchData(query) {
        const lowerQuery = query.toLowerCase();
      
        return data
          .map(section => {
            // Check if section name or desc matches
            const sectionMatch =
              section?.name?.toLowerCase().includes(lowerQuery) ||
              section?.desc?.toLowerCase().includes(lowerQuery);
      
            // Filter matching children (widgets)
            const matchingChildren = section.children?.filter(child =>
              child?.name?.toLowerCase().includes(lowerQuery) ||
              child?.desc?.toLowerCase().includes(lowerQuery)
            ) || [];
      
            // If section matches, return entire section (with all children)
            if (sectionMatch) {
              return { ...section };
            }
      
            // If only children match, return section with only matching children
            if (matchingChildren.length > 0) {
              return { ...section, children: matchingChildren };
            }
      
            // No match
            return null;
          })
          .filter(Boolean); // Remove nulls
    }

    const handleItemSelection = ({item, type}) => {
        switch(type){
            case "section":
                dispatch(setFocusElement({ type: "section", id: item.id, name: item.name }))
                setActiveTab(0);
                break;
            case "widget":
                dispatch(setFocusElement({ type: "ui", action: "edit", id: item.id, name: item.name }))
                setActiveTab(0);
                break;
            default:
                break;
        }

        setSelectedNodeId(item.id)
    }

    function highlightMatch(text, query) {
        if (!query) return text;
        const regex = new RegExp(`(${query})`, 'ig');
        const parts = text.split(regex);
      
        return parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <span key={i} style={{ backgroundColor: 'yellow'}}>{part}</span>
          ) : (
            part
          )
        );
      }

    useMemo(() => {
        if(debouncedSearchValue && debouncedSearchValue !== ""){
            setFilteredData(searchData(debouncedSearchValue))
        }else{
            setFilteredData(data)
        }
    },[debouncedSearchValue])

    useEffect(() => {
        setFilteredData(data)
    }, [data])


    return (
        <Paper id={"view_tree"} elevation={2} style={{ width: '100%', height: '100%', border: '0', boxShadow: 'none', paddingLeft: '16px', paddingTop: 0, paddingBottom: '10' }}>
            <TextField 
                  placeholder="Search"
                  variant="outlined"
                  size="small"
                  style={{ width: '100%' }}
                  onChange={(e) => setSearchValue(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ fontSize: 18, color: '#666' }} />
                      </InputAdornment>
                    ),
                    style: {
                      paddingTop: 2,
                      paddingBottom: 2,
                      height: 36,
                      margin: '8px 8px 8px 0px'
                    }
                  }}
                  inputProps={{
                    style: {
                      padding: '6px 8px',
                      fontSize: '13px'
                    }
                  }}
            />

             <TreeView
                aria-label="transaction-tree"
                defaultCollapseIcon={<ExpandMore />}
                defaultExpandIcon={<ChevronRight />}
                defaultExpanded={[...nodeIds]}
                selected={selectedNodeId}
            >
                {/* Sections */}
                <StyledTreeItem
                    nodeId="sections"
                    label={
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                            onClick={(e) => e.stopPropagation()} // Prevent collapse/expand on label click
                        >
                            <Typography style={{fontWeight: 600}}>
                                Sections ({data?.length || 0})
                            </Typography>
                            {/* <Tooltip title="Add clusterlets">
                                <IconButton
                                    classNamme={classes?.addClusterlet}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent TreeView selection
                                        // handleAddClusterlet(); // Your custom function
                                    }}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Tooltip> */}
                        </Box>
                    }
                >
                    {
                        filteredData?.map((section) => (
                            <StyledTreeItem
                                nodeId={section.id}
                                className={`view_section item_${section?.name}`}
                                key={section.id}
                                label={
                                    <Box width="100%" onClick={(e) => {
                                        e.stopPropagation();
                                        handleItemSelection({item: section, type: 'section'} );
                                    }}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Typography className={classes.smallText}>
                                                {highlightMatch(section?.desc || section?.name, debouncedSearchValue)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                }
                            >
                                {
                                    section?.children?.map(widget => (
                                        <StyledTreeItem
                                            nodeId={widget.id}
                                            key={widget.id}
                                            className={`view_widget item_${widget?.name}`}
                                            label={
                                                <Box width="100%" onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleItemSelection({item: widget, type: 'widget'} );
                                                }}>
                                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                                        <Typography className={classes.smallText}>
                                                            {highlightMatch(widget?.desc || widget?.name, debouncedSearchValue)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography className={classes.eventDescription}>
                                                        {widget.type}
                                                    </Typography>
                                                </Box>
                                            }
                                        >
                                        </StyledTreeItem>
                                    ))
                                }
                            </StyledTreeItem>
                        ))
                    }
                    {/* {renderClusterlets(transactionElements?.clusterlets)} */}
                </StyledTreeItem>
            </TreeView>
        </Paper>
    )
}

export default ViewTree;